import { useReducer } from 'react'
import type { Message } from '../types'

export interface ChatState {
  messages: Message[]
  input: string
  isTyping: boolean
  isRestoringConversation: boolean
  hasStartedChat: boolean
  selectedPortfolioId: string
  activeConversationId: string | null
  // Live in-progress assistant content during a stream. Empty string when no
  // stream is active. Updated on every content delta and rendered as an
  // in-progress bubble at the end of the message list, then cleared when the
  // final message is appended via 'append-message' on stream completion.
  streamingAssistantContent: string
}

export type ChatAction =
  | { type: 'set-input'; payload: string }
  | { type: 'set-typing'; payload: boolean }
  | { type: 'set-restoring'; payload: boolean }
  | { type: 'set-selected-portfolio'; payload: string }
  | { type: 'set-active-conversation'; payload: string | null }
  | { type: 'hydrate-conversation'; payload: { conversationId: string; portfolioId: string; messages: Message[] } }
  | { type: 'start-new-chat' }
  | { type: 'append-message'; payload: Message }
  | { type: 'append-messages'; payload: Message[] }
  | { type: 'restore-empty' }
  | { type: 'streaming-delta'; payload: string }
  | { type: 'streaming-replace'; payload: string }
  | { type: 'streaming-clear' }
  | { type: 'remove-last-exchange' }
  | { type: 'set-message-feedback'; payload: { messageId: string; feedback: 'up' | 'down' | null } }

export const initialChatState: ChatState = {
  messages: [],
  input: '',
  isTyping: false,
  isRestoringConversation: true,
  hasStartedChat: false,
  selectedPortfolioId: '',
  activeConversationId: null,
  streamingAssistantContent: '',
}

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'set-input':
      return { ...state, input: action.payload }
    case 'set-typing':
      return { ...state, isTyping: action.payload }
    case 'set-restoring':
      return { ...state, isRestoringConversation: action.payload }
    case 'set-selected-portfolio':
      return { ...state, selectedPortfolioId: action.payload }
    case 'set-active-conversation':
      return { ...state, activeConversationId: action.payload }
    case 'hydrate-conversation':
      return {
        ...state,
        activeConversationId: action.payload.conversationId,
        selectedPortfolioId: action.payload.portfolioId,
        messages: action.payload.messages,
        hasStartedChat: action.payload.messages.length > 0,
        streamingAssistantContent: '',
      }
    case 'start-new-chat':
      return {
        ...state,
        messages: [],
        input: '',
        hasStartedChat: false,
        selectedPortfolioId: '',
        activeConversationId: null,
        isTyping: false,
        streamingAssistantContent: '',
      }
    case 'append-message': {
      const messages = [...state.messages, action.payload]
      // Appending a finalized message implicitly ends any in-progress stream.
      return { ...state, messages, hasStartedChat: messages.length > 0, streamingAssistantContent: '' }
    }
    case 'append-messages': {
      const messages = [...state.messages, ...action.payload]
      return { ...state, messages, hasStartedChat: messages.length > 0 }
    }
    case 'streaming-delta':
      return { ...state, streamingAssistantContent: state.streamingAssistantContent + action.payload }
    case 'streaming-replace':
      return { ...state, streamingAssistantContent: action.payload }
    case 'streaming-clear':
      return { ...state, streamingAssistantContent: '' }
    case 'restore-empty':
      return {
        ...state,
        messages: [],
        hasStartedChat: false,
        activeConversationId: null,
      }
    case 'remove-last-exchange': {
      // Remove the last assistant (error) message and the user message before it.
      const msgs = [...state.messages]
      if (msgs.length >= 2 && msgs[msgs.length - 1].role === 'assistant' && msgs[msgs.length - 2].role === 'user') {
        msgs.splice(msgs.length - 2, 2)
      } else if (msgs.length >= 1 && msgs[msgs.length - 1].role === 'assistant') {
        msgs.pop()
      }
      return { ...state, messages: msgs }
    }
    case 'set-message-feedback': {
      const { messageId, feedback } = action.payload
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === messageId ? { ...m, feedback } : m,
        ),
      }
    }
    default:
      return state
  }
}

export function useChatReducer(initialConversationId: string | null) {
  // We only need to "restore" if there's actually a conversation to hydrate.
  // Defaulting to true would flash a "Restoring conversation..." spinner on
  // every fresh entry to TestOps Hub, even for deep links with just `?prompt=`.
  return useReducer(chatReducer, {
    ...initialChatState,
    activeConversationId: initialConversationId,
    isRestoringConversation: !!initialConversationId,
  })
}
